import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { userProfileService } from '../services/userProfileService';
import { ensureFirebaseInitialized } from '../firebase';
import logger from '../utils/logger';

const ProfileContext = createContext();

export function useProfile() {
  return useContext(ProfileContext);
}

export function ProfileProvider({ children }) {
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Capture uid as a stable primitive to avoid re-creating the callback
  // every time the currentUser object reference changes.
  const uid = currentUser?.uid;

  const loadProfile = useCallback(async () => {
    if (!uid) {
      setProfile(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Ensure Firebase is initialized
      await ensureFirebaseInitialized();

      // Try to get existing profile (getUserProfile fetches from nested member document)
      // Retry up to 3 times with delay to handle race conditions after account creation
      // where Firestore rules may not yet see the newly written user/family data
      let userProfile = null;
      let retries = 3;
      while (retries > 0) {
        try {
          userProfile = await userProfileService.getUserProfile(uid);
          break;
        } catch (err) {
          retries--;
          if (retries > 0 && err.message?.includes('permissions')) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          } else if (retries === 0) {
            userProfile = null;
          } else {
            userProfile = null;
            break;
          }
        }
      }

      // If no profile exists yet, use in-memory auth data for display.
      // IMPORTANT: Do NOT write a default profile to Firestore here.
      // The AccountCreationWizard is responsible for creating the user doc
      // and family membership.  Writing a default doc here would race with
      // the wizard and overwrite familyCode, role, groups, and isAdmin
      // that createFamily/joinFamily just set.
      if (!userProfile) {
        const displayName = currentUser.displayName || currentUser.email?.split('@')[0];
        const nameParts = (displayName || '').split(' ');

        userProfile = {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: displayName,
          firstName: nameParts[0] || 'User',
          lastName: nameParts.slice(1).join(' ') || '',
          familyCode: currentUser.familyCode || null,
          role: currentUser.role || 'member',
          isActive: true,
        };
      }

      setProfile(userProfile);
    } catch (err) {
      setError(err.message);
      logger.error('Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  useEffect(() => {
    if (uid) {
      loadProfile();
    } else {
      setProfile(null);
      setLoading(false);
    }
  }, [uid, loadProfile]);

  const updateProfile = async (updateData) => {
    if (!currentUser?.uid) {
      setError('User not authenticated');
      return;
    }

    try {
      setError(null);
      await userProfileService.updateProfile(currentUser.uid, updateData);
      await loadProfile(); // Reload profile after update
    } catch (err) {
      setError(err.message);
      logger.error('Error updating profile:', err);
    }
  };

  const value = {
    profile,
    loading,
    error,
    updateProfile,
    refreshProfile: loadProfile
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
} 